import type { SmpTicketModel, SmpOddsTicketModel } from '@nf/types/ticket';

import { BackLayPayoutSetting, OddsTicketModel, ParlayTicketModel, ParlayTicketsModel, TicketModel, BetTicketModel, BetParlayTicketsModel, BETTING_CODE_MAP, BettingCodeMap, BetMsgModel, HIDDEN_BETTING_CODES } from '@/hooks/query';
import { ViewEnum } from '@/types';
import { getDisplayOdds, getOddsKey, getOriginOdds, isPairOdds, removeCommas, revertParlayOddsToOriginOdds, calculatePayOut, calculateParlayPayOut, calculateBackLayPayOut, hkOdds } from '@/utils/odds-utils';
import { KafkaNote, WebOddsType } from '@nf/utils-common/constants';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';

const getPayoutByTicket = (ticket: TicketModel, oddsType: number, useVoucher?: string): number => {
	let payout = 0;

	if (ticket.stake) {
		const type = getPayoutType(ticket.Bettype, ticket.Betteam);

		const payoutOdds = Number(ticket.DisplayOdds);

		// FreeBet 負賠率轉 CN 時，API 會回 ticket.OddsType（CN）但user setting 的odds type 不會變，Freebet需用轉換後盤別計算 payout
		const shouldUseTicketOddsType =
			useVoucher === 'FreeBet' &&
			ticket.OddsType != null &&
			ticket.OddsType !== oddsType &&
			payoutOdds > 0;
		
		// oddstype is from user setting
		const convertedOddsType = shouldUseTicketOddsType ? ticket.OddsType! : oddsType;

		let payoutOddsType: number;
		if (ticket.isMMR) {
			payoutOddsType = 6;
		} else if (ticket.SportType == 134 || ticket.SportType == 116) {
			payoutOddsType = WebOddsType.DEC;
		} else {
			payoutOddsType = convertedOddsType || 1;
		}

		payout = ['profit', 'liability'].includes(type)
			? calculateBackLayPayOut(Number(ticket.DisplayOdds), ticket.stake)
			: calculatePayOut(
				payoutOdds,
				ticket.stake,
				payoutOddsType,
				ticket.PriceType);
	}

	return payout;
}

const getPayoutByParlayTicket = (ticket: ParlayTicketModel): number => {
	return ticket.stake ? calculateParlayPayOut(ticket.Odds, ticket.stake) : 0;
}

const getPayoutType = (betType: string, betTeam: string): '' | 'profit' | 'liability' => {
	const isCricketBackLay = BackLayPayoutSetting.betType.includes(betType);
	const isProfit = BackLayPayoutSetting.profitBetTeam.includes(betTeam);
	const isLiability = BackLayPayoutSetting.liabiltyBetTeam.includes(betTeam);

	if (isCricketBackLay) {
		if (isProfit) return 'profit';
		if (isLiability) return 'liability';
	}

	return '';
}

const getCustomCurrencyName = (useSabaCoin: boolean, currentCurrency?: string): string | undefined => {
	if (useSabaCoin) return 'SBC';
	return currentCurrency === 'SBC' ? undefined : currentCurrency;
};

const getTicketModel = (ticket: OddsTicketModel): TicketModel => {
	if (ticket.type === 'OU') {
		let line = '';
		if (ticket.Line) {
			line = ticket.Line;

			if ([28, 453, 477, 478].includes(ticket.bettype)) {
				if (ticket.betteam === 'x') {
					line = line.substring(line.length - 1, 1);
				}
			} else {
				line = line.replace(/\(|\)/gi, '');
			}
		}

		let matchId = ticket.gameid === 45 ? ticket.Parentid : ticket.Parentmatchid;
		matchId = matchId || ticket.Matchid;

		let Hdp1 = 0;
		let Hdp2 = 0;

		const floatLine = parseFloat(ticket.srcLine.toString());

		if (!isNaN(floatLine) && !ticket.isSBR) {
			Hdp1 = floatLine;
			if (floatLine < 0) {
				Hdp1 = 0;
				Hdp2 = Math.abs(floatLine);
			}
		} else {
			Hdp1 = ticket.Hdp1 || 0;
			Hdp2 = ticket.Hdp2 || 0;
		}

		const LeagueGroupId = ticket.LeagueGroupId ? Number(ticket.LeagueGroupId) : -1;

		if (ticket.bettype === 646) {
			Hdp2 = ticket.OtherHdp as number;
		}
		
		return {
			key: getOddsKey(ticket.type, ticket.oddsid, ticket.betteam),
			TicketType: ticket.type,
			SportType: ticket.gameid,
			Bettype: ticket.bettype.toString(),
			BettypeName: '',
			OddsID: ticket.oddsid,
			Betteam: ticket.betteam,
			PreDisplayOdds: '',
			DisplayOdds: ticket.odds,
			PreDisplayParlayOdds: '',
			DisplayParlayOdds: '',
			DisplayOddsPair: '',
			DisplayHDP: line,
			Matchid: matchId,
			HomeName: ticket.home,
			AwayName: ticket.away,
			LiveHomeScore: ticket.Hscore,
			LiveAwayScore: ticket.Ascore,
			//QuickBet: ticket.QuickBet, get from account model
			ChoiceValue: (ticket.ChoiceValue || '').toString(),
			isMMR: ticket.isMMR,
			MRPercentage: ticket.MRPercentage ? (ticket.MRPercentage as number).toString() : '',
			hasCashOut: !ticket.isMMR && ticket.isMatchHasCashOut,
			PriceType: ticket.pty,
			Hdp1: Hdp1,
			Hdp2: Hdp2,
			SrcOddsInfo: ticket.SrcOddsInfo,
			LeagueId: ticket.LeagueId,
			LeagueGroupId: LeagueGroupId,
			//default value
			stake: 0,
			payout: 0,
			minBet: 0,
			maxBet: 0,
			oldOdds: '',
			newOdds: '',
			AutoAcceptSec: '',
			AwayId: ticket.AwayId,
			Bet: '',
			BetHintMsg: '',
			BetID: '',
			BetRecommends: '',
			BetTimeConstraint: { EndTimestamp: 0 },
			BonusID: 0,
			BonusType: 0,
			Code: -99,
			DisplayTime: '',
			ErrorCode: 0,
			GameName: '',
			Guid: '',
			HasParlay: false,
			HomeId: ticket.HomeId,
			IsInPlay: false,
			IsLive: false,
			LeagueName: '',
			Line: line,
			NumberLine: ticket.NumberLine || 0,
			LiveScore: false,
			MatchCode: '',
			Message: '',
			Nolan: false,
			OddsBeforeOddsBoost: '',
			OddsBoost: false,
			OddsInfo: '',
			OddsStatus: '',
			originalPrice: ticket.originalPrice,
			PDHyalpsiD: '',
			ParentMatchid: ticket.Parentmatchid,
			Resourceid: String(ticket.Resourceid ?? ''),
			QuickBet: '1:100:10:1',
			RecommendType: 0,
			SeqNo: 0,
			SportName: '',
			SuggestStake: 0,
			Tenet: false,
			TicketTime: 0,
			UseBonus: 0,
			isLineChange: false,
			isOddsChange: false,
			isScoreChange: false,
			isSBR: ticket.isSBR ?? false,
			IsTest: ticket.IsTest,
			SubType: ticket.SubType,
			riaPsddOyalpsiD: '',
			sddOyalpsiD: '',
			sinfo: '',
			Maxbet: '',
			Minbet: '',
			CheckWaitingTicket: false,
			BetDelaySec: 0,
			sync: false
		};
	} else {
		/** Outright */
		return {
			key: getOddsKey(ticket.type, ticket.oddsid, ticket.betteam),
			TicketType: ticket.type,
			SportType: ticket.gameid,
			BettypeName: '',
			OddsID: ticket.oddsid,
			PreDisplayOdds: '',
			DisplayOdds: ticket.odds,
			PreDisplayParlayOdds: '',
			DisplayParlayOdds: '',
			DisplayOddsPair: '',
			HomeName: ticket.home,
			stake: 0,
			QuickBet: '1:100:10:1',
			ChoiceValue: (ticket.ChoiceValue || '').toString(),
			Bettype: '10',
			Betteam: '',
			DisplayHDP: '',
			Matchid: ticket.oddsid,
			AwayName: '',
			LiveHomeScore: 0,
			LiveAwayScore: 0,
			isMMR: ticket.isMMR,
			MRPercentage: ticket.MRPercentage ? (ticket.MRPercentage as number).toString() : '',
			hasCashOut: !ticket.isMMR && ticket.isMatchHasCashOut,
			PriceType: ticket.pty,
			Hdp1: 0,
			Hdp2: 0,
			SrcOddsInfo: ticket.SrcOddsInfo,
			LeagueId: ticket.LeagueId,
			LeagueGroupId: -1,
			//default value
			payout: 0,
			minBet: 0,
			maxBet: 0,
			oldOdds: '',
			newOdds: '',
			AutoAcceptSec: '',
			AwayId: ticket.AwayId,
			Bet: '',
			BetHintMsg: '',
			BetID: '',
			BetRecommends: '',
			BetTimeConstraint: { EndTimestamp: 0 },
			BonusID: 0,
			BonusType: 0,
			Code: -99,
			DisplayTime: '',
			ErrorCode: 0,
			GameName: '',
			Guid: '',
			HasParlay: false,
			HomeId: ticket.HomeId,
			IsInPlay: false,
			IsLive: false,
			LeagueName: '',
			Line: '0',
			LiveScore: false,
			MatchCode: '',
			Message: '',
			Nolan: false,
			OddsBeforeOddsBoost: '',
			OddsBoost: false,
			OddsInfo: '',
			OddsStatus: '',
			originalPrice: ticket.originalPrice,
			PDHyalpsiD: '',
			ParentMatchid: 0,
			RecommendType: 0,
			SeqNo: 0,
			SportName: '',
			SuggestStake: 0,
			Tenet: false,
			TicketTime: 0,
			UseBonus: 0,
			isLineChange: false,
			isOddsChange: false,
			isScoreChange: false,
			isSBR: false,
			riaPsddOyalpsiD: '',
			sddOyalpsiD: '',
			sinfo: '',
			Maxbet: '',
			Minbet: '',
			CheckWaitingTicket: false,
			BetDelaySec: 0,
			sync: false
		};
	}
}

const createSmpTicketModel = (ticket: SmpTicketModel, oddsTicket: SmpOddsTicketModel): TicketModel => {
	const stake = ticket.Bet ? Number(ticket.Bet) : 0;

	return {
		key: getOddsKey('OT', ticket.MarketId),
		TicketType: oddsTicket.type,
		SportType: oddsTicket.gameId,
		Bettype: String(oddsTicket.betType),
		BettypeName: ticket.BettypeName,
		OddsID: ticket.MarketId,
		Betteam: '',
		PreDisplayOdds: '',
		DisplayOdds: ticket.DisplayOdds,
		PreDisplayParlayOdds: '',
		DisplayParlayOdds: '',
		DisplayOddsPair: '',
		DisplayHDP: '',
		Matchid: ticket.Matchid,
		HomeName: ticket.HomeName,
		AwayName: ticket.AwayName,
		LiveHomeScore: ticket.LiveHomeScore,
		LiveAwayScore: ticket.LiveAwayScore,
		ChoiceValue: '',
		specialChoices: ticket.ChoiceValue,
		isMMR: false,
		MRPercentage: '',
		hasCashOut: false,
		PriceType: oddsTicket.pty,
		Hdp1: 0,
		Hdp2: 0,
		LeagueId: oddsTicket.leagueId,
		LeagueGroupId: 0,
		// default
		stake,
		payout: calculatePayOut(Number(ticket.DisplayOdds), stake, oddsTicket.pty),
		minBet: Number(removeCommas(ticket.Minbet)),
		maxBet: Number(removeCommas(ticket.Maxbet)),
		oldOdds: '',
		newOdds: '',
		AutoAcceptSec: '',
		AwayId: 0,
		Bet: ticket.Bet,
		BetHintMsg: '',
		BetID: '',
		BetRecommends: '',
		BetTimeConstraint: { EndTimestamp: 0 },
		BonusID: 0,
		BonusType: 0,
		Code: ticket.Code,
		DisplayTime: '',
		ErrorCode: 0,
		GameName: '',
		Guid: ticket.Guid,
		HasParlay: false,
		HomeId: 0,
		IsInPlay: false,
		IsLive: ticket.IsLive,
		LeagueName: ticket.LeagueName,
		Line: '',
		LiveScore: ticket.IsLive,
		MatchCode: '',
		Message: ticket.Message,
		Nolan: false,
		OddsBeforeOddsBoost: '',
		OddsBoost: false,
		OddsInfo: '',
		OddsStatus: '',
		originalPrice: Number(ticket.DisplayOdds),
		PDHyalpsiD: '',
		ParentMatchid: 0,
		QuickBet: ticket.QuickBet,
		RecommendType: 0,
		SeqNo: 0,
		SportName: ticket.SportName,
		SuggestStake: 0,
		Tenet: false,
		TicketTime: 0,
		UseBonus: 0,
		isLineChange: false,
		isOddsChange: false,
		isScoreChange: false,
		riaPsddOyalpsiD: '',
		sddOyalpsiD: '',
		sinfo: '',
		Maxbet: '',
		Minbet: '',
		CheckWaitingTicket: ticket.CheckWaitingTicket,
		BetDelaySec: ticket.BetDelaySec,
		sync: false
	};
};

const patchComboParlayTicketModel = (key: string, comboParlayTicket: ParlayTicketModel | undefined, dbComboParlayTicket: ParlayTicketModel, view: number): ParlayTicketModel => {
	const parlayName = dbComboParlayTicket?.Name;
	const newParlayName = view === ViewEnum.galaxy ? parlayName.substring(0, parlayName.indexOf(' (')) : parlayName;

	return {
		...dbComboParlayTicket,
		key,
		stake: dbComboParlayTicket?.Stake ? dbComboParlayTicket?.Stake : comboParlayTicket?.stake ?? 0,
		Name: newParlayName
	};
}

const patchTicketModel = (ticket: TicketModel, dbTicket: TicketModel, keepPreData?: boolean): TicketModel => {

	const preservedBettypeName = keepPreData && !dbTicket.BettypeName
		? ticket.BettypeName
		: dbTicket.BettypeName ?? ticket.BettypeName;
	const preservedChoiceValue = keepPreData && !dbTicket.ChoiceValue
		? ticket.ChoiceValue
		: dbTicket.ChoiceValue ?? ticket.ChoiceValue;
	// BB+ 盤口關閉時 GetComboTickets 只回部分欄位，空值就保留原本的值，避免畫面缺資料
	const keepPreIfEmpty = <T extends string | null | undefined>(dbValue: T, preValue: T) => (keepPreData && !dbValue ? preValue : dbValue);

	const stake = ticket.sync ? ticket.stake : Number(dbTicket.Bet);

	if (dbTicket.TicketType === 'OT') {
		return {
			...ticket,
			Code: dbTicket.Code,
			Common: dbTicket.Common,
			Message: dbTicket.Message,
			isOddsChange: dbTicket.isOddsChange,
			TicketType: dbTicket.TicketType,
			OddsID: dbTicket.OddsID,
			PreDisplayOdds: ticket.DisplayOdds,
			DisplayOdds: dbTicket.DisplayOdds ? removeCommas(dbTicket.DisplayOdds) : ticket.DisplayOdds,
			PreDisplayParlayOdds: ticket.DisplayParlayOdds,
			// 會在betslip store的流程用parlay ticket的值回填
			DisplayParlayOdds: '',
			stake: stake,
			QuickBet: dbTicket.QuickBet,
			ChoiceValue: preservedChoiceValue,
			SportType: dbTicket.SportType,
			SportName: dbTicket.SportName ? dbTicket.SportName : '',
			LeagueName: dbTicket.LeagueName ? dbTicket.LeagueName : '',
			sinfo: dbTicket.sinfo,
			Guid: dbTicket.Guid,
			TicketTime: dbTicket.TicketTime,
			RecommendType: dbTicket.RecommendType,
			minBet: Number(removeCommas(dbTicket.Minbet)),
			maxBet: Number(removeCommas(dbTicket.Maxbet)),
			sync: true,
			IsCashoutEnabled: dbTicket.IsCashoutEnabled ?? false,
			OddsType: dbTicket.OddsType || ticket.OddsType,
		};
	} else {
		return {
			...ticket,
			Code: dbTicket.Code,
			Common: dbTicket.Common,
			Message: dbTicket.Message,
			BettypeName: preservedBettypeName,
			TicketType: dbTicket.TicketType,
			Bettype: keepPreIfEmpty(dbTicket.Bettype, ticket.Bettype),
			OddsID: dbTicket.OddsID,
			PreDisplayOdds: ticket.DisplayOdds,
			DisplayOdds: dbTicket.DisplayOdds ? removeCommas(dbTicket.DisplayOdds) : ticket.DisplayOdds,
			DisplayHDP: dbTicket.DisplayHDP,
			Line: dbTicket.DisplayHDP,
			LiveScore: dbTicket.LiveScore,
			LiveHomeScore: dbTicket.LiveHomeScore,
			LiveAwayScore: dbTicket.LiveAwayScore,
			Matchid: dbTicket.Matchid,
			Betteam: dbTicket.Betteam ?? ticket.Betteam, //TODO: 待確認為什麼BB dbticket 沒有betteam
			stake: stake,
			QuickBet: dbTicket.QuickBet,
			ChoiceValue: preservedChoiceValue,
			HomeName: keepPreIfEmpty(dbTicket.HomeName, ticket.HomeName),
			AwayName: keepPreIfEmpty(dbTicket.AwayName, ticket.AwayName),
			SportType: dbTicket.SportType,
			isMMR: dbTicket.isMMR,
			MRPercentage: dbTicket.MRPercentage,
			SportName: dbTicket.SportName,
			LeagueName: keepPreIfEmpty(dbTicket.LeagueName, ticket.LeagueName),
			IsInPlay: dbTicket.IsInPlay,
			IsLive: dbTicket.IsLive,
			OddsInfo: keepPreIfEmpty(dbTicket.OddsInfo, ticket.OddsInfo),
			SrcOddsInfo: keepPreIfEmpty(dbTicket.SrcOddsInfo, ticket.SrcOddsInfo),
			Hdp1: dbTicket.Hdp1,
			Hdp2: dbTicket.Hdp2,
			BetID: dbTicket.BetID,
			BetTimeConstraint: dbTicket.BetTimeConstraint,
			BonusID: dbTicket.BonusID,
			BonusType: dbTicket.BonusType,
			sinfo: dbTicket.sinfo,
			RecommendType: dbTicket.RecommendType,
			GameName: dbTicket.GameName,
			Guid: dbTicket.Guid,
			HasParlay: dbTicket.HasParlay,
			TicketTime: dbTicket.TicketTime,
			minBet: Number(removeCommas(dbTicket.Minbet)),
			maxBet: Number(removeCommas(dbTicket.Maxbet)),
			LeagueGroupId: (dbTicket.LeagueGroupId || dbTicket.LeagueGroupId === 0) ? dbTicket.LeagueGroupId : -1,
			isLineChange: dbTicket.isLineChange,
			isOddsChange: dbTicket.isOddsChange,
			OddsBoost: dbTicket.OddsBoost,
			OddsBeforeOddsBoost: dbTicket.OddsBeforeOddsBoost,
			sync: true,
			IsCashoutEnabled: dbTicket.IsCashoutEnabled ?? false,
			OddsType: dbTicket.OddsType || ticket.OddsType,
			CategoryName: dbTicket.CategoryName
		};
	}
}

/**
 * SyncTickets (Multi/Parlay) flow helper:
 * - merge multi tickets with db tickets (index-based)
 * - backfill parlay display odds into single tickets
 * - recompute payout and apply SBC currency display name
 */
const patchSyncedMultiTickets = (
	tickets: Array<TicketModel>,
	dbMultiTickets: Array<TicketModel> | null | undefined,
	parlayData: ParlayTicketsModel | null | undefined,
	oddsType: number,
	isBBPlusMode: boolean,
	useSabaCoin: boolean,
	useVoucher?: string
): Array<TicketModel> => {
	return tickets.map((ticket, index) => {
		const dbTicket = dbMultiTickets ? dbMultiTickets[index] : null;
		const baseTicket = dbTicket && dbTicket.OddsID == ticket.OddsID
			? patchTicketModel(ticket, dbTicket, isBBPlusMode)
			: ticket;

		const dbParlayTicket = parlayData?.Tickets[index];
		// IMPORTANT: `tickets` may come from zustand+immer state (frozen in dev).
		// Never mutate `ticket`/`baseTicket` directly; always work on a new object.
		const nextTicket: TicketModel = { ...baseTicket };

		if (dbParlayTicket) {
			// 將db parlay ticket的值塞回singleTicket上
			nextTicket.PreDisplayParlayOdds = nextTicket.DisplayParlayOdds;

			if (dbParlayTicket.HasParlay) {
				// 如果該ticket支援parlay的時候直接使用ticket上的值
				nextTicket.DisplayParlayOdds = dbParlayTicket.DisplayOdds;
			} else {
				// 沒有的話用user odds 轉decimal odds(不支援parlay的話肯定不能下, 僅單純用於顯示
				const isPair = isPairOdds(Number(nextTicket.Bettype));

				let parlayOdds = nextTicket.DisplayOdds;
				if (isPair) {
					const originOdds = getOriginOdds(Number(parlayOdds), oddsType);
					parlayOdds = getDisplayOdds(originOdds, 1, ticket.PriceType, Number(ticket.Bettype));
				}

				nextTicket.DisplayParlayOdds = parlayOdds;
			}
		}

		nextTicket.CustomCurrencyName = getCustomCurrencyName(useSabaCoin, nextTicket.CustomCurrencyName);
		nextTicket.payout = getPayoutByTicket(nextTicket, oddsType, useVoucher);
		return nextTicket;
	});
};

/**
 * SyncTickets (Parlay comboData) flow helper:
 * - patch combo parlay tickets and preserve stake (when possible) from current parlay ticket model
 * - recompute payout for each combo parlay ticket
 */
const patchSyncedParlayData = (
	parlayData: ParlayTicketsModel | null | undefined,
	currentParlayTicket: ParlayTicketsModel | null | undefined,
	view: number
): ParlayTicketsModel | null => {
	if (!parlayData) return null;

	let newComboParlayTickets: Array<ParlayTicketModel> = [];

	if (parlayData?.comboData?.length) {
		newComboParlayTickets = parlayData.comboData.map(dbComboParlayTicket => {
			const key = `${parlayData.comboData.length}-${dbComboParlayTicket.Type}`;

			const comboParlayTicket = (currentParlayTicket?.comboData.length === parlayData?.comboData.length)
				? currentParlayTicket.comboData.find(comboParlayTicket => comboParlayTicket.key === key)
				: undefined;

			const newTicket = patchComboParlayTicketModel(key, comboParlayTicket, dbComboParlayTicket, view);
			return {
				...newTicket,
				payout: getPayoutByParlayTicket(newTicket),
			};
		});
	}
	// 儲存pre leg odds 以用來顯示odds change, 只在sync bet 中增加, 為了只顯示在使用者DoPlaceBet成功後
	const newLegOdds = parlayData?.LegOdds?.map((legOdd, index) => {
		return {
			...legOdd,
			PreOdds: currentParlayTicket?.LegOdds[index]?.Odds,
			shouldShowOddsChange: parlayData?.Tickets?.filter(ticket => ticket.Matchid === legOdd.MatchId).some(ticket => ticket.isOddsChange) // 若該bb plus group 中的ticket 有 odds change 則讓leg odds 顯示odds change
		};
	});
	return {
		...parlayData,
		comboData: newComboParlayTickets,
		LegOdds: newLegOdds || []
	};
};

/**
 * BB+ 同場同玩法不同球員時 Key、sinfo 都會重複，用 find 只會拿到第一筆
 * ItemList 順序同送出的 comboData[ItemList]，先用 index 對，sinfo 對不上再退回用 sinfo / Key 找
 */
const findBBPlusDbTicket = (
	dbMultiTickets: Array<BetTicketModel> | null | undefined,
	ticket: TicketModel,
	index: number
): BetTicketModel | undefined => {
	const indexedDbTicket = dbMultiTickets?.[index];
	if (indexedDbTicket?.sinfo === ticket.sinfo) {
		return indexedDbTicket;
	}

	return dbMultiTickets?.find(multiTicket => multiTicket.sinfo === ticket.sinfo || multiTicket.Key == ticket.key);
};

/**
 * BetTickets (Multi) flow helper:
 * - find corresponding db ticket by key
 * - patch fields from betting response
 * - recompute payout
 */
const patchBetSyncedMultiTickets = (
	tickets: Array<TicketModel>,
	dbMultiTickets: Array<BetTicketModel> | null | undefined,
	oddsType: number,
	useSabaCoin: boolean,
	type?: 'normal' | 'bb-plus',
	useVoucher?: string
): Array<TicketModel> => {
	return tickets.map((ticket, index) => {
		const dbTicket = type === 'bb-plus'
			? findBBPlusDbTicket(dbMultiTickets, ticket, index)
			: dbMultiTickets?.find(multiTicket => multiTicket.Key == ticket.key);
		const baseTicket = dbTicket ? patchBetTicketModel(ticket, dbTicket) : ticket;

		// IMPORTANT: `tickets` may come from zustand+immer state (frozen in dev).
		// Always return a fresh object before adding derived fields like `payout`.
		const customCurrencyName = getCustomCurrencyName(useSabaCoin, baseTicket.CustomCurrencyName);

		return {
			...baseTicket,
			payout: getPayoutByTicket(baseTicket, oddsType, useVoucher),
			CustomCurrencyName: customCurrencyName
		};
	});
};

/**
 * BetTickets (Parlay) flow helper:
 * - patch parlay Tickets from betting response ItemList
 * - backfill parlay odds (and special WEB-12051 behavior) into corresponding single tickets
 * - patch comboData and apply SBC currency display name
 */
const patchBetSyncedParlayData = (
	currentParlayTicket: ParlayTicketsModel | null | undefined,
	parlayData: BetParlayTicketsModel | null | undefined,
	singleTickets: Array<TicketModel>,
	dbMultiTickets: Array<BetTicketModel> | null | undefined,
	oddsType: number,
	view: number,
	useSabaCoin: boolean
): ParlayTicketsModel | null => {
	if (!parlayData) return null;

	let newComboParlayTickets: Array<ParlayTicketModel> = [];
	const newParlayTickets = currentParlayTicket?.Tickets?.map((ticket, index) => {
		const dbParlayTicket = parlayData?.ItemList[index];
		if (dbParlayTicket) {
			const dbSingleTicket = dbMultiTickets?.find(multiTicket => multiTicket.sinfo === dbParlayTicket.sinfo);
			// parlay ItemList ticket key就是matchId
			const singleTicket = singleTickets.find(ticket => ticket.Matchid === Number(dbParlayTicket.Key));

			if (singleTicket) {
				const bettype = Number(singleTicket.Bettype);
				// WEB-12051 如果用戶只下parlay沒有下對應的single ticket
				// 且下注沒有成功, 新增流程將parlay的資料加回single
				if (!dbSingleTicket && ![0, 1].includes(parlayData.Code)) {
					const originOdds = revertParlayOddsToOriginOdds(Number(dbParlayTicket.DisplayOdds), bettype);
					const odds = getDisplayOdds(originOdds, oddsType, singleTicket.PriceType, bettype);

					let code = dbParlayTicket.Code;

					if (dbParlayTicket.Code === 15) {
						singleTicket.isOddsChange = true;
						singleTicket.PreDisplayOdds = singleTicket.DisplayOdds;
						// code = -98;
					} else if (dbParlayTicket.Code === -99) {
						code = 0;
					}

					singleTicket.DisplayOdds = odds;
					singleTicket.Code = code;
					singleTicket.Message = dbParlayTicket.Message;
				}

				// 將db parlay ticket的值塞回singleTicket上
				singleTicket.PreDisplayParlayOdds = singleTicket.DisplayParlayOdds;

				if (dbParlayTicket.HasParlay) {
					// 如果該ticket支援parlay的時候直接使用ticket上的值
					singleTicket.DisplayParlayOdds = dbParlayTicket.DisplayOdds;
				} else {
					// 沒有的話用user odds 轉decimal odds(不支援parlay的話肯定不能下, 僅單純用於顯示
					const isPair = isPairOdds(bettype);

					let parlayOdds = singleTicket.DisplayOdds;
					if (isPair) {
						const originOdds = getOriginOdds(Number(parlayOdds), oddsType);
						parlayOdds = getDisplayOdds(originOdds, 1, ticket.PriceType, Number(ticket.Bettype));
					}

					singleTicket.DisplayParlayOdds = parlayOdds;
				}
			}

			// DoComboTicketsBet ParlayData 未回傳 LeagueGroupId，保留既有值避免leaguegroupid 變成 -1
			const patchedTicket = patchBetTicketModel(ticket, dbParlayTicket);
			const dbTicketHasLeagueGroupId = Boolean(dbParlayTicket.LeagueGroupId);

			if (dbTicketHasLeagueGroupId) {
				return patchedTicket;
			}
			return {
				...patchedTicket,
				...(!dbTicketHasLeagueGroupId ? {
					LeagueGroupId: singleTicket?.LeagueGroupId || ticket.LeagueGroupId,
				} : {}),
				SubType: singleTicket?.SubType ?? ticket.SubType,
			};
		}
		return ticket;
	}) || [];

	if (parlayData?.comboData?.length) {
		newComboParlayTickets = parlayData.comboData.map(dbComboParlayTicket => {
			const key = `${parlayData.comboData.length}-${dbComboParlayTicket.Type}`;

			const comboParlayTicket = (currentParlayTicket?.comboData.length === parlayData?.comboData.length)
				? currentParlayTicket.comboData.find(comboParlayTicket => comboParlayTicket.key === key)
				: undefined;

			const newTicket = patchComboParlayTicketModel(key, comboParlayTicket, dbComboParlayTicket, view);
			const nextTicket: ParlayTicketModel = {
				...newTicket,
				payout: getPayoutByParlayTicket(newTicket),
			};

			nextTicket.CustomCurrencyName = getCustomCurrencyName(useSabaCoin, nextTicket.CustomCurrencyName);
			return nextTicket;
		});
	}
	// 儲存pre leg odds 以用來顯示odds change, 只在sync bet 中增加, 為了只顯示在使用者DoPlaceBet成功後
	const newLegOdds = parlayData?.LegOdds?.map((legOdd, index) => {
		return {
			...legOdd,
			PreOdds: currentParlayTicket?.LegOdds[index]?.Odds,
			shouldShowOddsChange: true, // 下注時若有odds變化必須顯示odds change
		};
	});
	return ({
		...parlayData,
		Tickets: newParlayTickets,
		comboData: newComboParlayTickets,
		LegOdds: newLegOdds || [],
	} as unknown) as ParlayTicketsModel;
};

const patchBetTicketModel = (ticket: TicketModel, dbTicket: BetTicketModel): TicketModel => {
	return {
		...ticket,
		ACCode: dbTicket.ACCode,
		Code: dbTicket.Code,
		Message: dbTicket.Message,
		PreDisplayOdds: ticket.DisplayOdds,
		DisplayOdds: dbTicket.DisplayOdds ? removeCommas(dbTicket.DisplayOdds) : ticket.DisplayOdds,
		DisplayHDP: dbTicket.DisplayHDP,
		Line: dbTicket.DisplayHDP,
		LiveHomeScore: dbTicket.LiveHomeScore,
		LiveAwayScore: dbTicket.LiveAwayScore,
		IsInPlay: dbTicket.IsInPlay,
		IsLive: dbTicket.IsLive !== undefined ? dbTicket.IsLive : ticket.IsLive, // betting res 可能無此欄位, 此時沿用原始資料
		SrcOddsInfo: dbTicket.SrcOddsInfo,
		Hdp1: dbTicket.Hdp1,
		Hdp2: dbTicket.Hdp2,
		sinfo: dbTicket.sinfo,
		LeagueGroupId: (dbTicket.LeagueGroupId || dbTicket.LeagueGroupId === 0) ? dbTicket.LeagueGroupId : -1,
		CheckWaitingTicket: dbTicket.CheckWaitingTicket,
		Common: dbTicket.Common,
		ErrorCode: dbTicket.ErrorCode,
		TransId: dbTicket.TransId_Cash,
		isLineChange: dbTicket.isLineChange,
		isOddsChange: dbTicket.isOddsChange,
		OddsBeforeOddsBoost: dbTicket.OddsBeforeOddsBoost,
		RewardModel: dbTicket.UnderOverTicket?.RewardModel,
		OddsBoostType: dbTicket.UnderOverTicket?.OddsBoostType,
		sync: true
	};
}

const addCanBetMsg = (
	msgArray: BetMsgModel[],
	code: number,
	msg?: string,
	ticketKey?: string,
	skipShowToast: boolean = true
): BetMsgModel[] => {
	if (!BETTING_CODE_MAP.has(code)) {
		if (!msg || HIDDEN_BETTING_CODES.includes(code)) return msgArray;

		return [
			...msgArray,
			{
				ticketKey: ticketKey ?? '',
				code,
				msg,
				canBet: true
			}
		];
	}

	const setting = BETTING_CODE_MAP.get(code) as BettingCodeMap;

	if (skipShowToast && setting.showToast) return msgArray;

	return [
		...msgArray,
		{
			ticketKey: ticketKey ?? '',
			code,
			msg: msg || setting.msg || '',
			msgResourceKey: setting.msgResourceKey,
			canBet: setting.canBet,
			cssStatus: setting.cssStatus
		}
	];
}

/** kafka - mars parlay bet (when call place bet api) */
const setMarsParlayBetKafka = (view: ViewEnum, isParlayMode: boolean) => {
	if (view !== ViewEnum.mars) return;

	const kafkaData = {
		note: KafkaNote.MarsV1,
		type: 14,
		action: isParlayMode ? 2 : 1
	};
	setKafkaNormal(kafkaData);
}

/** kafka - mars bet button */
const setMarsBetKafka = (view: ViewEnum, isParlayMode: boolean) => {
	if (view !== ViewEnum.mars) return;

	const kafkaData = {
		note: KafkaNote.MarsV1,
		type: isParlayMode ? 11 : 10,
		action: isParlayMode ? 6 : 9
	}
	setKafkaNormal(kafkaData);
}

/** 組成呼叫 API 用的 Request Data */
const convertTicketToFormData = (formData: FormData, tickets: Array<TicketModel>, prefixLayer: string): FormData => {

	tickets.forEach((ticket, index) => {
		formData.append(`${prefixLayer}[${index}][type]`, ticket.TicketType);
		formData.append(`${prefixLayer}[${index}][bettype]`, ticket.Bettype);
		formData.append(`${prefixLayer}[${index}][oddsid]`, ticket.OddsID.toString());
		formData.append(`${prefixLayer}[${index}][odds]`, ticket.DisplayOdds);
		formData.append(`${prefixLayer}[${index}][Line]`, ticket.Line);
		formData.append(`${prefixLayer}[${index}][Hscore]`, ticket.LiveHomeScore.toString());
		formData.append(`${prefixLayer}[${index}][Ascore]`, ticket.LiveAwayScore.toString());
		formData.append(`${prefixLayer}[${index}][Matchid]`, ticket.Matchid.toString());
		formData.append(`${prefixLayer}[${index}][betteam]`, ticket.Betteam);
		formData.append(`${prefixLayer}[${index}][stake]`, (ticket.stake || 0).toString());
		formData.append(`${prefixLayer}[${index}][QuickBet]`, ticket.QuickBet);
		formData.append(`${prefixLayer}[${index}][ChoiceValue]`, ticket.ChoiceValue);
		formData.append(`${prefixLayer}[${index}][home]`, ticket.HomeName);
		formData.append(`${prefixLayer}[${index}][away]`, ticket.AwayName);
		formData.append(`${prefixLayer}[${index}][gameid]`, ticket.SportType.toString());
		formData.append(`${prefixLayer}[${index}][isMMR]`, (ticket.isMMR || false).toString());
		formData.append(`${prefixLayer}[${index}][MRPercentage]`, ticket.MRPercentage && !isNaN(Number(ticket.MRPercentage)) ? ticket.MRPercentage : '0');
		formData.append(`${prefixLayer}[${index}][GameName]`, ticket.GameName);
		formData.append(`${prefixLayer}[${index}][SportName]`, ticket.SportName);
		formData.append(`${prefixLayer}[${index}][IsInPlay]`, ticket.IsInPlay.toString());
		formData.append(`${prefixLayer}[${index}][isLive]`, (ticket.IsLive || false).toString());
		formData.append(`${prefixLayer}[${index}][SrcOddsInfo]`, ticket.SrcOddsInfo || '');
		formData.append(`${prefixLayer}[${index}][pty]`, (ticket.PriceType ?? 0).toString());
		formData.append(`${prefixLayer}[${index}][Hdp1]`, ticket.Hdp1.toString());
		formData.append(`${prefixLayer}[${index}][Hdp2]`, ticket.Hdp2.toString());
		formData.append(`${prefixLayer}[${index}][sinfo]`, ticket.sinfo);
		formData.append(`${prefixLayer}[${index}][hasCashOut]`, (ticket.hasCashOut || false).toString());
		formData.append(`${prefixLayer}[${index}][RecommendType]`, ticket.RecommendType.toString());
		formData.append(`${prefixLayer}[${index}][Minbet]`, (ticket.minBet || 0).toString());
		formData.append(`${prefixLayer}[${index}][LeagueGroupId]`, ticket.LeagueGroupId.toString());

		formData.append(`${prefixLayer}[${index}][Guid]`, ticket.Guid);
		formData.append(`${prefixLayer}[${index}][TicketTime]`, ticket.TicketTime.toString());
		// formData.append(`${prefixLayer}[${index}][IsFromParlayPage]`, 'true');
	});

	return formData;
}

export const BetslipService = {
	getPayoutByTicket,
	getPayoutByParlayTicket,
	getPayoutType,
	getTicketModel,
	createSmpTicketModel,
	patchTicketModel,
	patchSyncedMultiTickets,
	patchSyncedParlayData,
	patchBetSyncedMultiTickets,
	patchBetSyncedParlayData,
	patchBetTicketModel,
	patchComboParlayTicketModel,
	addCanBetMsg,
	setMarsParlayBetKafka,
	setMarsBetKafka,
	convertTicketToFormData
}